import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Table, Alert, Spin } from 'antd';
import moment from 'moment-jalaali';
import { useGeneralStore } from '../../store/slices/createGeneralSlice';
import { usePersistedStore } from '../../store/persisted-store';
import UserServices from '../../services/UserServices';
import strings from '../../utils/strings';
import StringHelper from '../helpers/StringHelper';
import ValidatorHelper from '../helpers/ValidatorHelper';
import OnlyNumber from '../shared/OnlyNumbers';

export default function ChangeMobileModal({ user, userName, onSuccess }) {
    const general = useGeneralStore();
    const { business } = usePersistedStore();
    const { token } = business.currentBusiness;
    const visible = general.view.changeMobileModal;

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [errorMsg, setErrorMsg] = useState(null);

    const currentMobile = user?.MobileNumber || user?.Username;
    const currentNationalCode = user?.NationalCode;
    const currentDisplayName = user?.DisplayName || (user?.FirstName ? `${user.FirstName} ${user.LastName || ''}`.trim() : '');

    useEffect(() => {
        if (visible && userName) {
            form.resetFields();
            if (currentNationalCode) {
                form.setFieldsValue({
                    NationalCode: String(currentNationalCode)
                });
            }
            setErrorMsg(null);
            fetchHistory();
        } else if (!visible) {
            setHistoryData([]);
            setErrorMsg(null);
        }
    }, [visible, userName, currentNationalCode]);

    const fetchHistory = () => {
        setHistoryLoading(true);
        UserServices.getUserMobileHistory(userName, token).then((res) => {
            const data = res.data?.Data || res.data?.Result || res.data?.data || res.data?.result || (Array.isArray(res.data) ? res.data : []);
            setHistoryData(Array.isArray(data) ? data : []);
            setHistoryLoading(false);
        }).catch((err) => {
            console.error(err);
            setHistoryData([]);
            setHistoryLoading(false);
        });
    };

    const validateMobileNotSame = (_, value) => {
        if (value && currentMobile && StringHelper.getEnglishNum(String(value).trim()) === StringHelper.getEnglishNum(String(currentMobile).trim())) {
            return Promise.reject(new Error('شماره موبایل جدید نمی‌تواند با شماره موبایل فعلی یکسان باشد'));
        }
        return Promise.resolve();
    };

    const onSubmit = (values) => {
        setLoading(true);
        setErrorMsg(null);

        const payload = {
            newMobileNumber: StringHelper.getEnglishNum(values.MobileNumber),
            newNationalCode: StringHelper.getEnglishNum(values.NationalCode)
        };

        UserServices.changeUserMobileNumber(userName, payload, token).then(() => {
            setLoading(false);
            general.NotifSuccess('شماره موبایل با موفقیت تغییر یافت');
            general.ToggleChangeMobileModal();
            if (onSuccess) {
                onSuccess();
            }
        }).catch((err) => {
            setLoading(false);
            const errorData = err.response?.data;
            const msg = errorData?.Message || errorData?.message || (typeof errorData === 'string' ? errorData : 'خطایی در تغییر شماره موبایل رخ داده است');
            setErrorMsg(msg);
        });
    };

    const columns = [
        {
            title: 'شماره موبایل قبلی',
            key: 'OldMobile',
            render: (_, record) => {
                const text = record.OldMobile || record.OldMobileNumber || record.oldMobile || record.oldMobileNumber || record.PreviousMobileNumber;
                return text ? strings.getFarsiNum(text) : '-';
            }
        },
        {
            title: 'شماره موبایل جدید',
            key: 'NewMobile',
            render: (_, record) => {
                const text = record.NewMobile || record.NewMobileNumber || record.newMobile || record.newMobileNumber;
                return text ? strings.getFarsiNum(text) : '-';
            }
        },
        {
            title: 'کد ملی',
            key: 'NationalCode',
            render: (_, record) => {
                const text = record.NationalCode || record.nationalCode;
                return text ? strings.getFarsiNum(text) : '-';
            }
        },
        {
            title: 'تاریخ و زمان تغییر',
            key: 'CreationTime',
            render: (_, record) => {
                const data = record.CreationTime || record.CreatedAt || record.CreatedDate || record.creationTime || record.createdAt;
                return data ? strings.getFarsiNum(moment(data).format(" jYYYY/jMM/jDD ساعت: HH:mm ")) : '-';
            }
        },
        {
            title: 'کاربر انجام دهنده',
            key: 'CreatedBy',
            render: (_, record) => record.CreatedBy || record.CreatedByUserName || record.CreatorUserName || record.Operator || record.createdBy || '-'
        }
    ];

    return (
        <Modal
            width={800}
            className="custom-table"
            title={`تغییر شماره موبایل مشتری (${userName || ''})`}
            open={visible}
            visible={visible}
            footer={false}
            destroyOnClose
            onCancel={() => general.ToggleChangeMobileModal()}
        >
            <Spin spinning={loading}>
                {/* نمایش مشخصات فعلی کاربر */}
                <div className="p-3 mb-3 bg-light rounded border">
                    <div className="row">
                        <div className="col-md-4 mb-2">
                            <span className="text-muted d-block font-size-12">نام مشتری:</span>
                            <strong>{currentDisplayName || '-'}</strong>
                        </div>
                        <div className="col-md-4 mb-2">
                            <span className="text-muted d-block font-size-12">شماره موبایل فعلی:</span>
                            <strong className="text-primary">{currentMobile ? strings.getFarsiNum(currentMobile) : '-'}</strong>
                        </div>
                        <div className="col-md-4 mb-2">
                            <span className="text-muted d-block font-size-12">کد ملی فعلی:</span>
                            <strong>{currentNationalCode ? strings.getFarsiNum(currentNationalCode) : 'ثبت نشده'}</strong>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <h5 className="font-size-14 mb-3">تاریخچه تغییرات شماره موبایل</h5>
                    <Table
                        columns={columns}
                        dataSource={historyData}
                        loading={historyLoading}
                        rowKey={(record) => record.Id || record.id || record.CreationTime || record.NewMobileNumber || record.NewMobile || String(Math.random())}
                        pagination={false}
                        bordered
                        size="small"
                        scroll={{ y: 200 }}
                        locale={{ emptyText: 'تاریخچه ای یافت نشد' }}
                    />
                </div>

                <h5 className="font-size-14 mb-3 mt-4">ثبت شماره موبایل جدید</h5>
                <Form form={form} layout="vertical" onFinish={onSubmit}>
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Item
                                name="MobileNumber"
                                label="شماره موبایل جدید"
                                rules={[
                                    ...ValidatorHelper.mobile(),
                                    { validator: validateMobileNotSame }
                                ]}
                            >
                                <Input
                                    placeholder="مثال: 09123456789"
                                    maxLength={11}
                                    onKeyPress={e => OnlyNumber(e)}
                                />
                            </Form.Item>
                        </div>
                        <div className="col-md-6">
                            <Form.Item
                                name="NationalCode"
                                label="کد ملی"
                                rules={[
                                    ...ValidatorHelper.required('کد ملی'),
                                    ...ValidatorHelper.nationalCode()
                                ]}
                            >
                                <Input
                                    placeholder="مثال: 0123456789"
                                    maxLength={10}
                                    onKeyPress={e => OnlyNumber(e)}
                                />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="text-right mt-3">
                        <Button onClick={() => general.ToggleChangeMobileModal()} className="mr-2">
                            انصراف
                        </Button>
                        <Button type="primary" htmlType="submit">
                            بررسی و ثبت اطلاعات
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
